import React, {useCallback, useEffect, useRef} from 'react';

export type HeatmapPoint = {x: number; y: number; value: number};

interface CanvasHeatmapProps {
    points: HeatmapPoint[];
    width: number;
    height: number;
    radius?: number;
    blur?: number;
    maxOpacity?: number;
    minOpacity?: number;
}

function CanvasHeatmap(props: CanvasHeatmapProps): JSX.Element {
    const {points, width, height, radius = 15, blur = 0.85, maxOpacity = 0.6, minOpacity = 0.15} = props;

    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const drawHeatmap = useCallback((): void => {
        const canvas = canvasRef.current;
        if (!canvas || points.length === 0) return;

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, width, height);

        const maxValue = Math.max(...points.map((p) => p.value), 1);

        points.forEach((point) => {
            const x = Math.round(point.x * width);
            const y = Math.round(point.y * height);
            const normalizedValue = point.value / maxValue;

            const opacity = minOpacity + normalizedValue * (maxOpacity - minOpacity);

            const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

            gradient.addColorStop(0, `rgba(255, 255, 255, ${opacity})`);
            gradient.addColorStop(blur, `rgba(255, 255, 255, ${opacity * 0.5})`);
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

            ctx.globalCompositeOperation = 'screen';
            ctx.fillStyle = gradient;
            ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
        });

        const imageData = ctx.getImageData(0, 0, width, height);
        const {data} = imageData;

        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha > 0) {
                const intensity = alpha / 255;
                const color = getHeatmapColor(intensity);
                data[i] = color.r;
                data[i + 1] = color.g;
                data[i + 2] = color.b;
                data[i + 3] = alpha;
            }
        }

        ctx.putImageData(imageData, 0, 0);
    }, [blur, height, maxOpacity, minOpacity, points, radius, width]);

    const getHeatmapColor = (intensity: number): {r: number; g: number; b: number} => {
        const colors = [
            {pos: 0.0, r: 0, g: 0, b: 255},
            {pos: 0.25, r: 0, g: 255, b: 255},
            {pos: 0.5, r: 0, g: 255, b: 0},
            {pos: 0.75, r: 255, g: 255, b: 0},
            {pos: 1.0, r: 255, g: 0, b: 0},
        ];

        let startColor = colors[0];
        let endColor = colors[colors.length - 1];

        for (let i = 0; i < colors.length - 1; i++) {
            if (intensity >= colors[i].pos && intensity <= colors[i + 1].pos) {
                startColor = colors[i];
                endColor = colors[i + 1];
                break;
            }
        }

        const range = endColor.pos - startColor.pos;
        const rangeIntensity = (intensity - startColor.pos) / range;

        return {
            r: Math.round(startColor.r + (endColor.r - startColor.r) * rangeIntensity),
            g: Math.round(startColor.g + (endColor.g - startColor.g) * rangeIntensity),
            b: Math.round(startColor.b + (endColor.b - startColor.b) * rangeIntensity),
        };
    };

    useEffect(() => {
        drawHeatmap();
    }, [drawHeatmap]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                width: '100%',
                height: '100%',
            }}
        />
    );
}

export default CanvasHeatmap;
